import React from 'react';
import { Star, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { DATASET_CATALOG } from '@/hooks/useReportBuilder';
import type { ReportTemplate } from '@/hooks/useReportBuilder';

interface Props {
  templates: ReportTemplate[];
  loading: boolean;
  onLoad: (t: ReportTemplate) => void;
  onDelete: (id: string) => void;
  onToggleFav: (id: string, val: boolean) => void;
}

const VISIBILITY_LABELS = {
  privado: '🔒 Privado',
  equipe: '👥 Equipe',
  contrato: '📋 Contrato',
};

export const TemplatesList: React.FC<Props> = ({ templates, loading, onLoad, onDelete, onToggleFav }) => {
  if (loading) {
    return <div className="space-y-2">{[1,2].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>;
  }

  if (templates.length === 0) {
    return (
      <p className="text-xs text-muted-foreground px-1 py-2 text-center">
        Nenhum relatório salvo para este contrato.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {templates.map(t => {
        const ds = DATASET_CATALOG.find(d => d.key === t.dataset);
        return (
          <div
            key={t.id}
            className="flex items-center gap-2 rounded-lg border border-border/50 bg-card px-3 py-2.5 group hover:border-primary/30 transition-colors"
          >
            <span className="text-base shrink-0">{ds?.icon ?? '📄'}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{t.nome}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Badge variant="outline" className="text-xs px-1 py-0 border-0 bg-muted">
                  {ds?.label ?? t.dataset}
                </Badge>
                <span className="text-xs text-muted-foreground">{VISIBILITY_LABELS[t.visibilidade]}</span>
              </div>
            </div>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onToggleFav(t.id, !t.is_favorito)}
                title={t.is_favorito ? 'Remover favorito' : 'Marcar favorito'}
              >
                <Star className={cn('h-3.5 w-3.5', t.is_favorito ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground')} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onLoad(t)}
                title="Carregar relatório"
              >
                <Upload className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onDelete(t.id)}
                title="Excluir"
              >
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
